import { useState } from "react";
import { Download, Expand } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

function payloadToSrc(payload) {
    const b64 = payload?.image_base64;
    if (!b64) return null;
    const mime = payload?.mime_type || "image/jpeg";
    return String(b64).startsWith("data:") ? b64 : `data:${mime};base64,${b64}`;
}

function extFromMime(mime) {
    if (!mime) return "jpg";
    if (mime.includes("png")) return "png";
    if (mime.includes("webp")) return "webp";
    if (mime.includes("gif")) return "gif";
    return "jpg";
}

function downloadImage(src, name) {
    const a = document.createElement("a");
    a.href = src;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

/** Renders a stored WhatsApp image message (base64 in payload). Click to preview / download. */
export default function MessageImage({ payload, alt = "Image", filename }) {
    const [open, setOpen] = useState(false);
    const p = payload || {};
    const src = payloadToSrc(p);
    const downloadName =
        filename || p.filename || `whatsapp-image-${Date.now()}.${extFromMime(p.mime_type)}`;

    if (!src) {
        return (
            <span className="text-xs opacity-70 italic">
                {p.media_id ? "Image (loading unavailable)" : "Image"}
            </span>
        );
    }

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="group relative block max-w-full rounded-md overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                title="Click to preview"
            >
                <img
                    src={src}
                    alt={alt}
                    className="max-w-full rounded-md max-h-64 object-contain bg-black/20 cursor-zoom-in transition-opacity group-hover:opacity-90"
                />
                <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <Expand className="size-6 text-white drop-shadow" />
                </span>
            </button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-[min(92vw,56rem)] p-0 gap-0 overflow-hidden">
                    <DialogHeader className="px-4 pt-4 pb-2">
                        <DialogTitle className="text-sm font-normal text-muted-foreground">
                            Image preview
                        </DialogTitle>
                    </DialogHeader>
                    <div className="px-4 pb-4 flex items-center justify-center bg-black/30 max-h-[75vh] overflow-auto">
                        <img
                            src={src}
                            alt={alt}
                            className="max-w-full max-h-[70vh] object-contain rounded-md"
                        />
                    </div>
                    <DialogFooter className="px-4 py-3 border-t border-border sm:justify-between gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={() => downloadImage(src, downloadName)}
                        >
                            <Download className="size-4" />
                            Download
                        </Button>
                        <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(false)}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

